import apiClient from './api';

/**
 * Channels Service
 *
 * API calls for social media channel management
 */

export type PlatformType =
  | 'LINKEDIN'
  | 'FACEBOOK'
  | 'INSTAGRAM'
  | 'TWITTER'
  | 'YOUTUBE'
  | 'TIKTOK'
  | 'PINTEREST'
  | 'THREADS'
  | 'GOOGLE_MY_BUSINESS'
  | 'BLUESKY'
  | 'CANVA'
  | 'GDRIVE'
  | 'WORDPRESS';

export interface Channel {
  id: number;
  platform: PlatformType;
  platformUsername: string;
  platformUserId: string;
  isActive: boolean;
  metadata?: {
    avatar?: string;
    followers?: number;
    displayName?: string;
    lastTokenError?: string;
    [key: string]: unknown;
  };
}

export const channelsService = {
  /**
   * Get all channels for the authenticated user
   * @param opts.includeInactive - when true, also returns inactive/disconnected channels
   */
  getChannels: async (opts?: { includeInactive?: boolean }): Promise<Channel[]> => {
    const params = opts?.includeInactive ? { includeInactive: 'true' } : undefined;
    const response = await apiClient.get<{ success: boolean; data: Channel[] }>(
      '/api/channels',
      { params }
    );
    return response.data.data;
  },

  /**
   * Get a single channel by ID
   */
  getChannel: async (id: number): Promise<Channel> => {
    const response = await apiClient.get<{ data: Channel }>(
      `/api/channels/${id}`
    );
    return response.data.data;
  },

  /**
   * Disconnect a channel
   */
  disconnectChannel: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/channels/${id}`);
  },

  /**
   * Initiate LinkedIn OAuth flow
   */
  connectLinkedIn: async (): Promise<string> => {
    const response = await apiClient.get<{ data: { authUrl: string } }>(
      '/api/auth/linkedin'
    );
    return response.data.data.authUrl;
  },

  /**
   * Initiate Facebook OAuth flow
   * This also connects Instagram Business accounts linked to Facebook Pages
   */
  connectFacebook: async (): Promise<string> => {
    const response = await apiClient.get<{ data: { authUrl: string } }>(
      '/api/auth/facebook'
    );
    return response.data.data.authUrl;
  },

  /**
   * Get pending LinkedIn connection data
   * Called after OAuth redirect with session ID
   */
  getLinkedInPending: async (sessionId: string): Promise<{
    profile: {
      id: string;
      firstName: string;
      lastName: string;
      profilePicture?: string;
      email?: string;
    };
    organizations: Array<{
      id: string;
      name: string;
      vanityName?: string;
      logo?: string;
      urn: string;
    }>;
  }> => {
    const response = await apiClient.get<{
      data: {
        profile: {
          id: string;
          firstName: string;
          lastName: string;
          profilePicture?: string;
          email?: string;
        };
        organizations: Array<{
          id: string;
          name: string;
          vanityName?: string;
          logo?: string;
          urn: string;
        }>;
      };
    }>(`/api/auth/linkedin/pending/${sessionId}`);
    return response.data.data;
  },

  /**
   * Connect selected LinkedIn channels
   * Saves the user's channel selection after OAuth
   */
  connectLinkedInChannels: async (payload: {
    sessionId: string;
    selectedChannels: Array<{ type: 'profile' | 'organization'; id?: string }>;
  }): Promise<{ channels: Channel[] }> => {
    const response = await apiClient.post<{ data: { channels: Channel[] } }>(
      '/api/auth/linkedin/connect',
      payload
    );
    return response.data.data;
  },

  /**
   * Get pending connection data for any platform (generic)
   * Called after OAuth redirect with session ID
   */
  getPendingConnection: async (platform: string, sessionId: string): Promise<{
    profile?: {
      id: string;
      firstName?: string;
      lastName?: string;
      name?: string;
      profilePicture?: string;
      email?: string;
    };
    entities: Array<{
      id: string;
      name: string;
      vanityName?: string;
      logo?: string;
      urn?: string;
      metadata?: Record<string, unknown>;
    }>;
  }> => {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        profile?: {
          id: string;
          firstName?: string;
          lastName?: string;
          name?: string;
          profilePicture?: string;
          email?: string;
        };
        entities: Array<{
          id: string;
          name: string;
          vanityName?: string;
          logo?: string;
          urn?: string;
          metadata?: Record<string, unknown>;
        }>;
      };
    }>(`/api/auth/${platform}/pending/${sessionId}`);
    return response.data.data;
  },

  /**
   * Connect selected channels for any platform (generic)
   * Saves the user's channel selection after OAuth
   */
  connectChannels: async (platform: string, payload: {
    sessionId: string;
    selectedChannels: Array<{ type: string; id?: string }>;
  }): Promise<{ channels: Channel[] }> => {
    const response = await apiClient.post<{ data: { channels: Channel[] } }>(
      `/api/auth/${platform}/connect`,
      payload
    );
    return response.data.data;
  },
};

/**
 * Platform configuration
 */
export const PLATFORM_CONFIG: Record<
  PlatformType,
  {
    name: string;
    charLimit: number;
    color: string;
    icon: string;
  }
> = {
  LINKEDIN: {
    name: 'LinkedIn',
    charLimit: 3000,
    color: '#0A66C2',
    icon: 'linkedin',
  },
  FACEBOOK: {
    name: 'Facebook',
    charLimit: 63206,
    color: '#1877F2',
    icon: 'facebook',
  },
  INSTAGRAM: {
    name: 'Instagram',
    charLimit: 2200,
    color: '#E4405F',
    icon: 'instagram',
  },
  TWITTER: {
    name: 'Twitter',
    charLimit: 280,
    color: '#1DA1F2',
    icon: 'twitter',
  },
  YOUTUBE: {
    name: 'YouTube',
    charLimit: 5000,
    color: '#FF0000',
    icon: 'youtube',
  },
  TIKTOK: {
    name: 'TikTok',
    charLimit: 2200,
    color: '#000000',
    icon: 'music',
  },
  PINTEREST: {
    name: 'Pinterest',
    charLimit: 500,
    color: '#E60023',
    icon: 'pin',
  },
  THREADS: {
    name: 'Threads',
    charLimit: 500,
    color: '#000000',
    icon: 'at-sign',
  },
  GOOGLE_MY_BUSINESS: {
    name: 'Google My Business',
    charLimit: 1500,
    color: '#4285F4',
    icon: 'map-pin',
  },
  BLUESKY: {
    name: 'Bluesky',
    charLimit: 300,
    color: '#0085FF',
    icon: 'cloud',
  },
  CANVA: {
    name: 'Canva',
    charLimit: 0,
    color: '#7D2AE8',
    icon: 'canva',
  },
  GDRIVE: {
    name: 'Google Drive',
    charLimit: 0,
    color: '#4285F4',
    icon: 'folder',
  },
  WORDPRESS: {
    name: 'WordPress',
    charLimit: 0,
    color: '#21759B',
    icon: 'blog-content',
  },
};
